/**
 * `<PointCloudModeSwitch />` — Wave-5 / A10.
 *
 * Segmented toggle that lets the operator pivot between
 * "Combined Scene" (Giro3D) and "Point Cloud Only" (Potree) inside a
 * single gTwin project. Hidden when `availableModes.length < 2` so the
 * existing UI is unchanged for scenes that have a single render path
 * (G8 reversibility).
 *
 * Spec references:
 *   - `docs/design-briefs/A10-pointcloud-mode-switch.md` — state matrix,
 *     state-preservation contract, primitive checklist.
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 — tokens
 *     only, single primary CTA per row, 40 px touch min, focus visible,
 *     non-color cue per state (chip icon + aria-pressed).
 *
 * Implementation note: the design system does NOT expose a
 * `SegmentedButton` primitive today. Per the masterplan kickoff brief
 * the fallback contract is "two pressable chips with toggled
 * `aria-pressed`" — that's what this component renders. This avoids
 * introducing a new visual primitive (G6) and reuses the existing
 * `<Chip pressable />` API consumed elsewhere in the point-cloud tool
 * card.
 */

import type { CSSProperties } from 'react';
import { Chip, Icon, tokens, type ChipVariant } from '@/design';
import {
  CoordinateModeBadge,
} from '@/components/three-d/CoordinateModeBadge';
import type { SceneMode } from '@/app/sceneModeController';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PointCloudModeSwitchProps {
  /** Modes available for the current scene-package. When length < 2 the
   *  control renders `null`. */
  availableModes: ReadonlyArray<SceneMode>;
  /** Active mode. */
  current: SceneMode;
  /** Called when the operator picks a different mode. Never invoked while
   *  `disabled` is true. */
  onChange: (mode: SceneMode) => void;
  /** When true, both chips render `aria-disabled="true"` and clicks do
   *  not invoke `onChange`. Used by the host during the engine swap so
   *  the operator cannot double-click. */
  disabled?: boolean;
  /** When true, append the shared `<CoordinateModeBadge mode="local" />`
   *  after the segmented control. Matches the cross-card "local frame"
   *  cue (`gps_off` glyph + "Local frame" label). */
  localEngineering?: boolean;
  /** Test id consumed by Playwright + Vitest specs. Defaults to
   *  `point-cloud-mode-switch`. */
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Mode metadata — token-only, non-color cue per mode
// ---------------------------------------------------------------------------

interface ModeMeta {
  readonly label: string;
  readonly icon: 'view_in_ar' | 'blur_on';
  readonly description: string;
}

const MODE_TABLE: Record<SceneMode, ModeMeta> = {
  'combined-scene': {
    label: 'Combined scene',
    icon: 'view_in_ar',
    description: 'Render point cloud alongside models, terrain, and overlays',
  },
  'point-cloud-only': {
    label: 'Point cloud only',
    icon: 'blur_on',
    description: 'Render only the point cloud through the specialist engine',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PointCloudModeSwitch({
  availableModes,
  current,
  onChange,
  disabled = false,
  localEngineering = false,
  'data-testid': testId,
}: PointCloudModeSwitchProps) {
  if (availableModes.length < 2) return null;

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.space[2],
  };

  const groupStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    padding: tokens.space[1],
    background: tokens.color.surface[1],
    borderRadius: tokens.radius.md,
    border: tokens.border.thin,
    minHeight: tokens.density.touchMin,
  };

  return (
    <section
      aria-label="Point cloud render mode"
      data-testid={testId ?? 'point-cloud-mode-switch'}
      style={containerStyle}
    >
      <div role="group" aria-label="Render mode segmented control" style={groupStyle}>
        {availableModes.map((mode) => {
          const meta = MODE_TABLE[mode];
          const pressed = mode === current;
          const variant: ChipVariant = pressed ? 'info' : 'neutral';
          return (
            <Chip
              key={mode}
              label={meta.label}
              icon={meta.icon}
              variant={variant}
              pressable
              pressed={pressed}
              onClick={() => {
                if (disabled) return;
                if (mode === current) return;
                onChange(mode);
              }}
              aria-label={`${meta.label} — ${meta.description}`}
              data-testid={`point-cloud-mode-switch-${mode}`}
            />
          );
        })}
      </div>
      {localEngineering ? (
        <CoordinateModeBadge
          mode="local"
          data-testid="point-cloud-mode-switch-local-frame-badge"
        />
      ) : null}
      {/* Visually-hidden disabled cue — `aria-disabled` is read by screen
          readers but sighted users need a non-color cue too. The host
          controls visibility by toggling `disabled`; we surface a small
          inline status string so the cue is not color-only. */}
      {disabled ? (
        <span
          role="status"
          aria-live="polite"
          data-testid="point-cloud-mode-switch-switching"
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            color: tokens.color.ink.secondary,
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
          }}
        >
          <Icon name="pending" size={16} decorative />
          Switching engine…
        </span>
      ) : null}
    </section>
  );
}
